// The live layer of comms for one site: the socket, the per-channel live
// buckets, sending with optimistic rows, retry and discard, the cache patches
// an incoming frame owes, and the read-ack reporter. Each app's
// ChatSocketProvider calls this and provides the result through ChatContext.
//
// One WS per site (not per channel). Switching channels in the UI is a free
// re-read of a different bucket — no reconnect, no history refetch beyond
// TanStack Query's cache.
//
// What differs by platform is handed in, not branched on: how a socket opens,
// where read acks wait, how a pending file is uploaded, how held files are
// previewed, and what "watched" means.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { ChannelsResponse, HistoryResponse } from '../../apis/comms.js';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import type { ChatStatus, ClientFrame, CommsMessage, MessageSegment, ServerFrame } from '../../interfaces/ICommsApi.js';
import { applyReceiptToCaches, applyReceiptToMessages } from '../../utils/comms/applyReceiptToCaches.js';
import { derivePreviewText } from '../../utils/comms/derivePreviewText.js';
import { otherDmMemberId } from '../../utils/comms/dmChannelId.js';
import type { ChatSocketHandlers } from '../ChatSocket.js';
import { commsKeys } from '../queryKeys.js';
import { ReadReporter, type IReadQueueStore } from '../ReadReporter.js';
import type { IPendingAttachment } from '../../interfaces/ICommsApi.js';
import type { IChatContextValue } from './ChatContext.js';
import type { ILocalAttachments } from './useLocalAttachments.js';

/** The socket as this hook drives it — the shared `ChatSocket`, or a test double. */
export interface ILiveSocket {
  open(): void;
  close(): void;
  send(frame: ClientFrame): boolean;
  setWatched(watched: boolean): void;
}

/**
 * Options for `useChatConnection`.
 * currentUser: Who is sending — stamped on optimistic rows and used to recognise our own echoes.
 * createSocket: How a socket opens on this platform (cookie on the web, bearer on the phone).
 * readQueueStore: Where read acks wait out a closed app, per user and site.
 * local: This platform's holder of files not yet confirmed (the web mints preview URLs).
 * upload: Store one pending file for a channel; resolves to the server's id for it, rejects when it cannot be sent.
 * onIncomingMessage: Someone else's message arrived for a channel: bump its unread count.
 * watched: When given, forwarded to the socket so it can sleep while nobody is looking; omit when the socket decides itself.
 */
export interface IUseChatConnectionOptions {
  siteId: string;
  currentUser: { id: string; username: string } | null | undefined;
  createSocket: (siteId: string, handlers: ChatSocketHandlers) => ILiveSocket;
  readQueueStore: (userId: string, siteId: string) => IReadQueueStore;
  local: ILocalAttachments;
  upload: (siteId: string, channelId: string, file: IPendingAttachment) => Promise<{ id: string }>;
  onIncomingMessage: (channelId: string) => void;
  watched?: boolean;
}

function nextClientMessageId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useChatConnection({
  siteId,
  currentUser,
  createSocket,
  readQueueStore,
  local,
  upload,
  onIncomingMessage,
  watched,
}: IUseChatConnectionOptions): IChatContextValue {
  const queryClient = useQueryClient();
  const { openDm, deleteAttachment } = useClientApis().comms;
  const releaseLocal = local.release;
  const [liveMessagesByChannel, setLiveMessagesByChannel] = useState<Record<string, CommsMessage[]>>({});
  const [status, setStatus] = useState<ChatStatus>('closed');
  const socketRef = useRef<ILiveSocket | null>(null);

  // In-flight "ensure this DM channel is persisted" calls, keyed by channel
  // id — a rapid double-send on a still-virtual DM must not fire openDm twice.
  const dmEnsureInFlight = useRef<Map<string, Promise<void>>>(new Map());

  // Files already uploaded for a message the server hasn't accepted yet, by
  // clientMessageId — Retry sends these instead of uploading the bytes again.
  const uploadedRef = useRef<Map<string, { id: string }[]>>(new Map());

  /** Forget a message's uploaded files and delete them from the server. */
  const dropUploads = useCallback((clientMessageId: string) => {
    for (const ref of uploadedRef.current.get(clientMessageId) ?? []) void deleteAttachment(siteId, ref.id);
    uploadedRef.current.delete(clientMessageId);
  }, [siteId, deleteAttachment]);

  // Stable refs for the current user and the unread callback — avoids
  // re-creating the WS just because an object reference changed.
  const userIdRef = useRef(currentUser?.id ?? 'me');
  const userNameRef = useRef(currentUser?.username ?? 'me');
  const onIncomingRef = useRef(onIncomingMessage);
  useEffect(() => {
    userIdRef.current = currentUser?.id ?? 'me';
    userNameRef.current = currentUser?.username ?? 'me';
    onIncomingRef.current = onIncomingMessage;
  }, [currentUser, onIncomingMessage]);

  const handleFrame = useCallback((frame: ServerFrame) => {
    if (frame.type === 'receipt') {
      // Someone read messages: move the counters behind the marks and any
      // open info view, in place — in the history and in the live bucket,
      // where a message sent this session still lives.
      applyReceiptToCaches(queryClient, siteId, frame);
      setLiveMessagesByChannel((prev) => {
        const bucket = prev[frame.channelId];
        if (!bucket) return prev;
        const next = applyReceiptToMessages(bucket, frame);
        return next === bucket ? prev : { ...prev, [frame.channelId]: next };
      });
      return;
    }

    if (frame.type === 'message') {
      const incoming = frame.message;

      // Bump the unread counter for this channel. Skip our own echoes
      // (we already saw the message via the optimistic insert),
      // deletion broadcasts (already counted on arrival), and edit
      // broadcasts (message was already counted; only content changed).
      if (incoming.authorId !== userIdRef.current && !incoming.deletedAt && !incoming.editedAt) {
        onIncomingRef.current(incoming.channelId);
      }

      // Patch the cached channel list so the sidebar preview updates
      // immediately — no refetch needed.
      queryClient.setQueryData<ChannelsResponse>(
        commsKeys.channels(siteId),
        (cached) => {
          if (!cached) return cached;
          const known = cached.channels.some((ch) => ch.id === incoming.channelId);
          if (!known) {
            // Message arrived for a channel not yet in the sidebar (e.g. a peer
            // just opened a DM with us). Invalidate so the list refetches and
            // the new entry appears — we can't construct the full summary here.
            void queryClient.invalidateQueries({ queryKey: commsKeys.channels(siteId) });
            return cached;
          }
          return {
            channels: cached.channels.map((ch) =>
              ch.id !== incoming.channelId ? ch : {
                ...ch,
                lastMessageAt: incoming.createdAt,
                lastMessagePreview: {
                  text: derivePreviewText(incoming),
                  authorId: incoming.authorId,
                  authorName: incoming.authorName,
                  createdAt: incoming.createdAt,
                },
              },
            ),
          };
        },
      );

      // Patch the history cache so messages loaded from REST also update
      // in place (e.g. a deletion broadcast for an older message).
      queryClient.setQueryData<InfiniteData<HistoryResponse>>(
        commsKeys.history(siteId, incoming.channelId),
        (cached) => {
          if (!cached) return cached;
          const pages = cached.pages.map((page) => {
            const idx = page.messages.findIndex((m) => m.id === incoming.id);
            if (idx === -1) return page;
            const messages = page.messages.slice();
            messages[idx] = incoming;
            return { ...page, messages };
          });
          return { ...cached, pages };
        },
      );

      // Confirmed: the row now shows the server's attachments, and its uploads belong to the message.
      if (incoming.clientMessageId && incoming.authorId === userIdRef.current) {
        releaseLocal(incoming.clientMessageId);
        uploadedRef.current.delete(incoming.clientMessageId);
      }

      setLiveMessagesByChannel((prev) => {
        const bucket = prev[incoming.channelId] ?? [];
        // If the id is already in the bucket, update it in place — this
        // covers deletion broadcasts (same id, deletedAt now set) and any
        // other server-side mutations on a known message.
        const existingIndex = bucket.findIndex((m) => m.id === incoming.id);
        if (existingIndex !== -1) {
          const next = bucket.slice();
          next[existingIndex] = incoming;
          return { ...prev, [incoming.channelId]: next };
        }
        // Optimistic replacement: the server echoes our clientMessageId,
        // so swap the matching pending row in place.
        if (incoming.clientMessageId) {
          const pendingIndex = bucket.findIndex(
            (m) => m.clientMessageId === incoming.clientMessageId && m.status === 'pending',
          );
          if (pendingIndex !== -1) {
            const next = bucket.slice();
            next[pendingIndex] = incoming;
            return { ...prev, [incoming.channelId]: next };
          }
        }
        return { ...prev, [incoming.channelId]: [...bucket, incoming] };
      });
      return;
    }

    if (frame.type === 'error' && frame.clientMessageId) {
      const failedId = frame.clientMessageId;
      // `invalid` may be the files themselves (expired, already used): upload fresh on Retry.
      // `internal` is the server's own failure: Retry reuses what's uploaded.
      if (frame.code === 'invalid') dropUploads(failedId);
      // The error frame doesn't carry channelId, so we mark the matching
      // pending row in whatever bucket holds it. clientMessageId is a
      // UUID, so collisions across buckets are not a concern.
      setLiveMessagesByChannel((prev) => {
        let touched = false;
        const next: Record<string, CommsMessage[]> = {};
        for (const [channelId, bucket] of Object.entries(prev)) {
          let bucketTouched = false;
          const newBucket = bucket.map((m) => {
            if (m.clientMessageId === failedId && m.status === 'pending') {
              bucketTouched = true;
              return { ...m, status: 'failed' as const };
            }
            return m;
          });
          next[channelId] = bucketTouched ? newBucket : bucket;
          if (bucketTouched) touched = true;
        }
        return touched ? next : prev;
      });
    }
  }, [siteId, queryClient, releaseLocal, dropUploads]);

  // What the reader has seen, on its way to the server. Persisted per user
  // and site so a closed app cannot lose evidence; flushed whenever the
  // socket (re)opens, since a closed socket keeps the ids queued.
  const reporterRef = useRef<ReadReporter | null>(null);

  useEffect(() => {
    const reporter = new ReadReporter({
      send: (channelId, messageIds) =>
        socketRef.current?.send({ type: 'read', channelId, messageIds }) ?? false,
      store: readQueueStore(userIdRef.current, siteId),
    });
    reporterRef.current = reporter;
    void reporter.resume();

    // A socket that reopens missed what was said while it was down (a locked
    // phone, a dropped network, a laptop lid): refetch the conversation list
    // and every loaded history — the list key is the prefix of each history
    // key — so the open conversation shows those messages before they're
    // marked read.
    let wasOpen = false;
    const socket = createSocket(siteId, {
      onFrame: handleFrame,
      onStatus: (next) => {
        setStatus(next);
        if (next !== 'open') return;
        reporter.flush();
        if (wasOpen) void queryClient.invalidateQueries({ queryKey: commsKeys.channels(siteId) });
        wasOpen = true;
      },
    });
    socketRef.current = socket;
    socket.open();

    return () => {
      reporter.flush();
      reporter.dispose();
      socket.close();
      socketRef.current = null;
      reporterRef.current = null;
    };
  }, [siteId, handleFrame, queryClient, createSocket, readQueueStore]);

  useEffect(() => {
    if (watched !== undefined) socketRef.current?.setWatched(watched);
  }, [watched, siteId]);

  const reportRead = useCallback((channelId: string, messageIds: string[]) => {
    reporterRef.current?.report(channelId, messageIds);
  }, []);

  const setStatusOf = useCallback(
    (channelId: string, clientMessageId: string, from: CommsMessage['status'], to: CommsMessage['status']) => {
      setLiveMessagesByChannel((prev) => {
        const bucket = prev[channelId];
        if (!bucket) return prev;
        return {
          ...prev,
          [channelId]: bucket.map((m) => (m.clientMessageId === clientMessageId && m.status === from ? { ...m, status: to } : m)),
        };
      });
    },
    [],
  );

  // A virtual (not-yet-persisted) DM has no backend record to publish into —
  // this is the one place that actually creates it, exactly once, right
  // before the first message goes out. Already-real channels (the common
  // case) skip straight past via the cache check below at essentially no
  // cost. Returns false if the ensure itself failed.
  const ensureChannelExists = useCallback(
    async (channelId: string): Promise<boolean> => {
      const cached = queryClient.getQueryData<ChannelsResponse>(commsKeys.channels(siteId));
      if (cached?.channels.some((c) => c.id === channelId)) return true;
      const targetUserId = otherDmMemberId(channelId, siteId, userIdRef.current);
      
      // Not a DM-shaped id we can lazily create (e.g. a group channel) —
      // proceed as before; publish() will surface the real error below if
      // the channel genuinely doesn't exist.
      if (!targetUserId) return true;

      let inFlight = dmEnsureInFlight.current.get(channelId);
      if (!inFlight) {
        inFlight = openDm(siteId, targetUserId).then((res) => {
          // Seed the list cache immediately so the sidebar shows the now-real
          // DM without waiting on a refetch.
          queryClient.setQueryData<ChannelsResponse>(commsKeys.channels(siteId), (prev) => {
            if (!prev) return prev;
            if (prev.channels.some((c) => c.id === res.channel.id)) return prev;
            return { channels: [res.channel, ...prev.channels] };
          });
        });
        dmEnsureInFlight.current.set(channelId, inFlight);
        // A second, independent consumer of `inFlight` — `.finally` re-throws
        // on rejection, and this chain isn't the one reporting the failure
        // (the `await` below is), so swallow it here to avoid an unhandled
        // rejection while still letting the awaiting caller see the error.
        inFlight
          .finally(() => {
            if (dmEnsureInFlight.current.get(channelId) === inFlight) {
              dmEnsureInFlight.current.delete(channelId);
            }
          })
          .catch(() => {});
      }
      try {
        await inFlight;
        return true;
      } catch {
        return false;
      }
    },
    [siteId, queryClient, openDm],
  );

  // Upload the files, then publish. The WS frame is only sent after every
  // file is stored — this is what keeps other users from seeing the message
  // before the attachments are ready. Any failure marks the row failed; the
  // row keeps its files so Retry can run this again under the same id, which
  // the server de-duplicates — reusing uploads that already succeeded.
  const deliver = useCallback(
    async (channelId: string, clientMessageId: string, segments: MessageSegment[], pendingFiles: IPendingAttachment[]) => {
      const markFailed = () => setStatusOf(channelId, clientMessageId, 'pending', 'failed');
      if (!(await ensureChannelExists(channelId))) {
        markFailed();
        return;
      }

      let attachmentRefs = uploadedRef.current.get(clientMessageId) ?? [];
      if (attachmentRefs.length === 0 && pendingFiles.length > 0) {
        const results = await Promise.allSettled(pendingFiles.map((f) => upload(siteId, channelId, f)));
        const fulfilled = results.filter(
          (r): r is PromiseFulfilledResult<{ id: string }> => r.status === 'fulfilled',
        );
        if (fulfilled.length < results.length) {
          for (const r of fulfilled) void deleteAttachment(siteId, r.value.id);
          markFailed();
          return;
        }
        attachmentRefs = fulfilled.map((r) => ({ id: r.value.id }));
        uploadedRef.current.set(clientMessageId, attachmentRefs);
      }

      // Not sent (no socket, or it's still reconnecting): fail the row so it offers Retry, which reuses the uploads.
      const sent = socketRef.current?.send({
        type: 'send',
        channelId,
        clientMessageId,
        segments,
        attachments: attachmentRefs,
      });
      if (!sent) markFailed();
    },
    [siteId, ensureChannelExists, setStatusOf, upload, deleteAttachment],
  );

  const holdLocal = local.hold;
  const readLocal = local.read;

  const send = useCallback(
    (channelId: string, segments: MessageSegment[], pendingFiles: IPendingAttachment[]) => {
      const clientMessageId = nextClientMessageId();

      // Optimistic insert — user sees their message immediately. Its files
      // are held locally so the row can show them; the WS echo from the
      // server (after upload + publish) carries the real descriptors and
      // swaps this row out.
      const optimistic: CommsMessage = {
        id: clientMessageId,
        channelId,
        authorId: userIdRef.current,
        authorName: userNameRef.current,
        segments,
        attachments: [],
        createdAt: new Date().toISOString(),
        clientMessageId,
        status: 'pending',
      };
      if (pendingFiles.length > 0) holdLocal(clientMessageId, pendingFiles);
      setLiveMessagesByChannel((prev) => {
        const bucket = prev[channelId] ?? [];
        return { ...prev, [channelId]: [...bucket, optimistic] };
      });

      void deliver(channelId, clientMessageId, segments, pendingFiles);
    },
    [deliver, holdLocal],
  );

  /** Send a failed message again: same text, same files, same id. */
  const retry = useCallback(
    (message: CommsMessage) => {
      if (!message.clientMessageId) return;
      setStatusOf(message.channelId, message.clientMessageId, 'failed', 'pending');
      void deliver(message.channelId, message.clientMessageId, message.segments, readLocal(message.clientMessageId));
    },
    [deliver, readLocal, setStatusOf],
  );

  /** Drop a failed message and the files it was holding. */
  const discard = useCallback(
    (message: CommsMessage) => {
      if (!message.clientMessageId) return;
      releaseLocal(message.clientMessageId);
      dropUploads(message.clientMessageId);
      setLiveMessagesByChannel((prev) => {
        const bucket = prev[message.channelId];
        if (!bucket) return prev;
        return { ...prev, [message.channelId]: bucket.filter((m) => m.clientMessageId !== message.clientMessageId) };
      });
    },
    [releaseLocal, dropUploads],
  );

  const localAttachments = local.byClientMessageId;
  
  return useMemo<IChatContextValue>(
    () => ({ siteId, status, liveMessagesByChannel, localAttachments, send, retry, discard, reportRead }),
    [siteId, status, liveMessagesByChannel, localAttachments, send, retry, discard, reportRead],
  );
}
